import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';

import { GENERATION_CONFIG } from '@/widgets/create-model/constants/generation-config';

import { useAuthStore } from '@/features/auth/model/auth-store';

import { personaService } from '@/shared/api';
import { MediaGraphQLService } from '@/shared/api/media-graphql-service';
import type { PersonaData } from '@/shared/api/persona-service';
import { useInfinitePersonas } from '@/shared/hooks/use-infinite-personas';
import { logger } from '@/shared/lib/logger';
import { useModalStore } from '@/shared/stores/modal-store';
// import { useQueryClient } from '@tanstack/react-query';
import { BaseModal, Button, MultipleDropZone } from '@/shared/ui';

export const UploadImagesModal = () => {
  const { isModalOpen, closeModal, getModalData } = useModalStore();
  const { refetch } = useInfinitePersonas();
  // const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{
    isUploading: boolean;
    current: number;
    total: number;
    currentFileName: string;
  }>({
    isUploading: false,
    current: 0,
    total: 0,
    currentFileName: '',
  });

  const isOpen = isModalOpen('upload-images');
  const modalData = getModalData('upload-images');
  const persona = modalData?.persona as PersonaData | undefined;
  const currentDatasetCount = (modalData?.currentDatasetCount as number) ?? 0;
  const onUploadSuccess = modalData?.onUploadSuccess as (() => Promise<void>) | undefined;

  // Calculate max images allowed (MAX_DATASET_SIZE - current dataset count)
  const maxImages = Math.max(0, GENERATION_CONFIG.MAX_DATASET_SIZE - currentDatasetCount);

  const handleClose = useCallback(() => {
    // Clean up blob URLs
    previewImages.forEach((url) => {
      if (url.startsWith('blob:')) {
        URL.revokeObjectURL(url);
      }
    });

    closeModal('upload-images');
    setError(null);
    setPreviewImages([]);
    setPendingFiles([]);
    setUploadProgress({
      isUploading: false,
      current: 0,
      total: 0,
      currentFileName: '',
    });
  }, [previewImages, closeModal]);

  const handleImagesUpload = useCallback((files: File[]) => {
    // Create blob URLs for preview
    const newImageUrls = files.map((file) => URL.createObjectURL(file));
    setPreviewImages((prev) => [...prev, ...newImageUrls]);
    setPendingFiles((prev) => [...prev, ...files]);
  }, []);

  const handleImageRemove = useCallback(
    (index: number) => {
      const imageUrl = previewImages[index];
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }

      setPreviewImages((prev) => prev.filter((_, i) => i !== index));
      setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    },
    [previewImages],
  );

  const handleUpload = async () => {
    if (!persona || pendingFiles.length === 0) return;

    const authState = useAuthStore.getState();
    const userId = authState.user?.id;

    if (!userId) {
      toast.error('User not authenticated');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      setUploadProgress({
        isUploading: true,
        current: 0,
        total: pendingFiles.length,
        currentFileName: '',
      });

      const result = await personaService.uploadMultiplePersonaMedia(
        pendingFiles,
        persona,
        userId,
        (current, total, fileName) => {
          setUploadProgress({
            isUploading: true,
            current,
            total,
            currentFileName: fileName,
          });
        },
      );

      // If some images were uploaded successfully, refresh dataset
      if (result.successful.length > 0) {
        try {
          // Refresh dataset by getting updated medias from GraphQL
          await MediaGraphQLService.getMediaPersonas(persona.id);

          // Refetch personas to get updated data from server
          await refetch();

          // Invalidate dataset query to refresh the data (done by callback now)

          // Call the callback to refresh dataset state on the model page
          if (onUploadSuccess) {
            logger.ui.debug('Calling onUploadSuccess callback to refresh dataset state');
            await onUploadSuccess();
          } else {
            logger.ui.warn('No onUploadSuccess callback provided');
          }
        } catch (error) {
          logger.ui.error('Failed to refresh dataset', { error });
        }
      }

      // Always close modal
      handleClose();

      // Only show error toast messages (no success messages)
      if (result.failed.length > 0) {
        toast.error(`Failed to upload ${result.failed.length} images`);
      }
    } catch (error) {
      // Always close modal and show toast on general error too
      handleClose();
      toast.error(error instanceof Error ? error.message : 'Failed to upload images');
    } finally {
      setIsLoading(false);
      setUploadProgress({
        isUploading: false,
        current: 0,
        total: 0,
        currentFileName: '',
      });
    }
  };

  if (!persona) return null;

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Upload Images to ${persona.name}`}
      size="2xl"
      className="!max-h-[90vh]"
    >
      <div className="space-y-4 w-full max-h-[calc(90vh-120px)] overflow-y-auto px-2">
        {/* Info Text */}
        <p className="text-zinc-400 text-sm">
          You can upload up to <span className="font-medium text-white">{maxImages}</span> more
          images ({currentDatasetCount} already in dataset).
        </p>

        {/* Drop Zone */}
        <MultipleDropZone
          images={previewImages}
          maxImages={maxImages}
          onImagesUpload={handleImagesUpload}
          onImageRemove={handleImageRemove}
          isLoading={uploadProgress.isUploading}
          uploadedFileNames={[]}
          existingImagesCount={currentDatasetCount}
          width="min(600px, 70vw)"
        />

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Upload Progress */}
        {uploadProgress.isUploading && (
          <div className="p-3 bg-violet-500/10 border border-violet-500/20 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-4 h-4 border-2 border-violet-400 border-t-transparent rounded-full animate-spin"></div>
              </div>
              <div className="flex-1">
                <p className="text-violet-400 text-sm font-medium">
                  Uploading {uploadProgress.current} of {uploadProgress.total} images...
                </p>
                <p className="text-violet-300 text-xs mt-1">
                  Current: {uploadProgress.currentFileName}
                </p>
              </div>
            </div>
            <div className="mt-2 bg-violet-500/20 rounded-full h-1.5">
              <div
                className="bg-violet-500 h-1.5 rounded-full transition-all duration-300"
                style={{
                  width: `${uploadProgress.total > 0 ? (uploadProgress.current / uploadProgress.total) * 100 : 0}%`,
                }}
              />
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            onClick={handleClose}
            disabled={isLoading || uploadProgress.isUploading}
            variant="secondary"
            size="md"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleUpload}
            disabled={isLoading || uploadProgress.isUploading || pendingFiles.length === 0}
            variant="primary"
            size="md"
            className="flex-1"
          >
            {uploadProgress.isUploading
              ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
              : `Upload ${pendingFiles.length} Image${pendingFiles.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};
